/* eslint-disable @typescript-eslint/no-use-before-define */ import { Chat } from "@ai-sdk/react";
import { TRANSCRIPT_CHUNK_TYPE } from "@juristic/types/models/chat";
import { UiDataPartType } from "@juristic/types/stream/ai/data_events";
import { DefaultChatTransport } from "ai";
import { ulid } from "ulid";
const MAX_RETRIES = 5;
const RETRY_DELAY_MS = 500;
const STOP_RECHECK_MS = 300;
const BEFORE_SEND_WAIT_MS = 15_000;
const delay = (ms)=>new Promise((resolve)=>{
        setTimeout(resolve, ms);
    });
const isReplayOf = (chunk, card)=>{
    if (card.type !== UiDataPartType.ToolHook || chunk.type !== UiDataPartType.ToolHook) return false;
    const data = chunk.data;
    return data.type === card.data.type && data.toolCallId === card.data.toolCallId;
};
function findLast(items, predicate) {
    for(let index = items.length - 1; index >= 0; index--){
        if (predicate(items[index])) return items[index];
    }
    return undefined;
}
const hasToolCall = (message, toolCallId)=>message.parts.some((part)=>"toolCallId" in part && part.toolCallId === toolCallId || "data" in part && part.data?.toolCallId === toolCallId);
const isSettled = (message)=>message?.role !== "assistant" || message.metadata?.status !== undefined && message.metadata.status !== "streaming";
export function createChatSession({ url, chatId, fetch, body, onError, onData, beforeSend }) {
    const transcripts = new Map();
    const lifetime = new AbortController();
    let pendingInput = null;
    let retries = 0;
    let retryTimer;
    let heldMessage;
    let isDisposed = false;
    const post = (path, payload)=>fetch(`${url}${path}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(payload),
            signal: lifetime.signal
        });
    const withOrigin = (path, params = {})=>{
        const query = new URLSearchParams(params);
        Object.entries(body()).forEach(([key, value])=>{
            if (value !== null && value !== undefined && typeof value !== "object") {
                query.set(key, String(value));
            }
        });
        const search = query.toString();
        return search ? `${url}${path}?${search}` : `${url}${path}`;
    };
    const transport = new DefaultChatTransport({
        api: `${url}/messages`,
        fetch,
        prepareSendMessagesRequest: async ({ messages, body: requestBody })=>{
            if (beforeSend) {
                const ready = beforeSend().catch((error)=>console.warn("[chat] before send failed:", error));
                await Promise.race([
                    ready,
                    delay(BEFORE_SEND_WAIT_MS)
                ]);
            }
            const parentId = findLast(messages.slice(0, -1), (message)=>message.role === "assistant")?.id ?? null;
            return {
                body: {
                    ...requestBody,
                    ...body(messages[messages.length - 1]),
                    messages: messages.slice(-1),
                    parentId
                }
            };
        },
        prepareReconnectToStreamRequest: ()=>({
                api: withOrigin(`/messages/${chat.lastMessage?.id}/stream`, {
                    startIndex: "0"
                })
            })
    });
    const reconnect = transport.reconnectToStream.bind(transport);
    transport.reconnectToStream = async (options)=>{
        const seed = chat.lastMessage;
        const stream = await reconnect(options);
        if (!stream) {
            if (isSettled(seed) || !scheduleRetry(load)) setTimeout(releaseHeldMessage);
            return null;
        }
        if (!seed) return stream;
        const runPartOffset = seed.metadata?.runPartOffset ?? 0;
        const priorRunParts = seed.parts.slice(0, runPartOffset);
        const pendingCard = seed.parts.slice(runPartOffset).find((part)=>part.type === UiDataPartType.ToolHook && part.data.toolCallId === pendingInput?.toolCallId);
        let seededCard;
        let isSeeded = false;
        return stream.pipeThrough(new TransformStream({
            transform (chunk, controller) {
                const active = chat.activeResponse;
                if (!isSeeded && active) {
                    isSeeded = true;
                    const parts = structuredClone(pendingCard ? [
                        ...priorRunParts,
                        pendingCard
                    ] : priorRunParts);
                    active.state.message.parts = parts;
                    active.state.message.metadata = seed.metadata;
                    seededCard = pendingCard && parts[parts.length - 1];
                }
                if (active && seededCard && isReplayOf(chunk, seededCard)) {
                    active.state.message.parts = active.state.message.parts.filter((part)=>part !== seededCard);
                    seededCard = undefined;
                }
                controller.enqueue(chunk);
            }
        }));
    };
    const applyTranscript = (transcript)=>{
        chat.messages = chat.messages.map((message)=>message.id === transcript.message.id ? transcript.message : message);
        pendingInput = transcript.pendingInput;
    };
    const markUnsent = ()=>chat.setStatus({
            status: "error",
            error: new Error("unsent-message")
        });
    function releaseHeldMessage() {
        if (!heldMessage || isDisposed) return;
        chat.messages = [
            ...chat.messages,
            heldMessage
        ];
        heldMessage = undefined;
        markUnsent();
    }
    const load = async (shouldResume = true)=>{
        const local = heldMessage ?? chat.lastMessage;
        const response = await fetch(withOrigin(""), {
            signal: lifetime.signal
        });
        if (isDisposed) return false;
        if (!response.ok) throw Object.assign(new Error(`Failed to load chat: ${response.status}`), {
            status: response.status
        });
        const snapshot = await response.json();
        if (isDisposed) return false;
        await chat.stop();
        if (isDisposed) return false;
        const isUnsent = local?.role === "user" && !snapshot.messages.some((message)=>message.id === local.id);
        const isLeafStreaming = shouldResume && snapshot.messages[snapshot.messages.length - 1]?.metadata?.status === "streaming";
        heldMessage = isUnsent && isLeafStreaming ? local : undefined;
        pendingInput = snapshot.pendingInput;
        if (isUnsent && !isLeafStreaming) {
            chat.messages = [
                ...snapshot.messages,
                local
            ];
            markUnsent();
            return true;
        }
        chat.messages = snapshot.messages;
        chat.clearError();
        if (isLeafStreaming) chat.resumeStream().catch(handleError);
        return isUnsent;
    };
    async function resume() {
        clearTimeout(retryTimer);
        retryTimer = undefined;
        if (chat.lastMessage?.role === "assistant") {
            await chat.resumeStream();
        } else {
            await load();
        }
    }
    function scheduleRetry(retry) {
        if (isDisposed || retryTimer !== undefined || retries >= MAX_RETRIES) return false;
        retryTimer = setTimeout(()=>{
            retryTimer = undefined;
            retry().catch(handleError);
        }, RETRY_DELAY_MS * 2 ** retries);
        retries += 1;
        return true;
    }
    function handleError(error) {
        if (error instanceof TypeError) scheduleRetry(resume);
        onError(error);
    }
    const chat = new Chat({
        id: chatId,
        generateId: ulid,
        transport,
        onError: (error)=>{
            releaseHeldMessage();
            if (/"(stale-parent|duplicate-message|not-on-branch|turn-running)"/.test(error.message)) {
                load().catch(handleError);
            }
            handleError(error);
        },
        onData: (part)=>{
            const { type, data } = part;
            if (type !== TRANSCRIPT_CHUNK_TYPE && !isDisposed) onData?.({
                type,
                data
            });
            if (type !== TRANSCRIPT_CHUNK_TYPE || isDisposed) return;
            const transcript = data;
            const streaming = chat.activeResponse?.state.message;
            if (transcript.message.id === streaming?.id) {
                transcripts.set(transcript.message.id, transcript);
            } else {
                applyTranscript(transcript);
            }
        },
        onFinish: ({ isAbort, isDisconnect, isError })=>{
            if (isDisposed) return;
            transcripts.forEach(applyTranscript);
            transcripts.clear();
            releaseHeldMessage();
            if (isDisconnect || !isAbort && !isSettled(chat.lastMessage)) {
                scheduleRetry(resume);
            } else if (!isAbort && !isError) {
                retries = 0;
            }
        }
    });
    const send = async (...args)=>{
        clearTimeout(retryTimer);
        retryTimer = undefined;
        retries = 0;
        const wasSubmitted = chat.status === "submitted";
        if (wasSubmitted || chat.status === "streaming") await chat.stop();
        if (wasSubmitted) await load(false);
        if (isDisposed) return;
        await chat.sendMessage(...args);
    };
    const stop = async ()=>{
        await chat.stop();
        if (chat.lastMessage?.role !== "assistant" && await load(false)) {
            await delay(STOP_RECHECK_MS);
            if (!isDisposed) await load(false);
        }
        const target = chat.lastMessage;
        if (isDisposed || target?.role !== "assistant" || isSettled(target)) return;
        const response = await post(`/messages/${target.id}/stop`, {});
        if (isDisposed) return;
        if (!response.ok) throw new Error(`Failed to stop message: ${response.status}`);
        const stored = await response.json();
        if (isDisposed) return;
        chat.messages = chat.messages.map((message)=>message.id === stored.id ? {
                ...message,
                parts: stored.parts,
                metadata: {
                    ...message.metadata,
                    status: stored.status
                }
            } : message);
    };
    const answerCard = async (toolCallId, payload, attempt = 0)=>{
        const messageId = pendingInput?.toolCallId === toolCallId ? pendingInput.messageId : (findLast(chat.messages, (message)=>hasToolCall(message, toolCallId)) ?? chat.lastMessage)?.id;
        const response = await post(`/messages/${messageId}/parts/${toolCallId}`, payload);
        if (isDisposed) return false;
        if (!response.ok && response.status !== 409) {
            const detail = await response.text().catch(()=>"");
            throw new Error(`Failed to answer card: ${response.status} ${detail}`);
        }
        const { status, ended: hasEnded } = await response.json();
        if (isDisposed) return false;
        if (!status && response.ok) return !hasEnded;
        if (status === "not-ready" && attempt < MAX_RETRIES) {
            await delay(RETRY_DELAY_MS);
            if (isDisposed) return false;
            return answerCard(toolCallId, payload, attempt + 1);
        }
        if (status === "resolved") return true;
        if (status === "continued") {
            await load();
            return true;
        }
        if (status === "interrupted" || status === "already-answered") {
            await load();
            return false;
        }
        throw new Error(`Failed to answer card: ${status ?? response.status}`);
    };
    const dispose = ()=>{
        isDisposed = true;
        clearTimeout(retryTimer);
        lifetime.abort();
        chat.stop().catch(onError);
    };
    return {
        chat,
        load,
        resume,
        send,
        stop,
        answerCard,
        dispose
    };
}
